import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { getProjectBySlug } from "../data/projects";
import { getServiceBySlug } from "../data/services";

const SITE_URL = "https://zotaris.com";
const SITE_NAME = "Zotaris";
const DEFAULT_DESCRIPTION =
   "Zotaris construieste infrastructura digitala pentru businessuri: site-uri, platforme custom, automatizari AI, workflow-uri, dashboard-uri, UI/UX si consultanta digitala.";
const DEFAULT_KEYWORDS =
   "Zotaris, automatizari AI, web development Moldova, site-uri, platforme custom, UI UX, dashboard, CRM, digitalizare business, workflow automation, React, WordPress, consultanta digitala";
const OG_IMAGE = `${SITE_URL}/android-chrome-512x512.png`;

const projectTitles = {
   horizonauto: "Horizon Auto",
   stm: "STM Performance",
   fruconomy: "Fruconomy",
   apo: "APO",
   euroaccent: "Euroaccent",
   nivaspet: "Nivaspet",
   daydriver: "DayDriver",
   barbarosa: "Barbarosa School",
   stratus: "Stratus Lofts",
   glg: "Scoala Auto GLG",
   seven11: "7Eleven Agency",
   vapeoutlet: "Vape Outlet",
   bantleads: "Bantleads",
   maruldeaur: "Marul de Aur",
   mambolichior: "Mambo Lichior",
   noidivin: "Noi Divin",
   insuladistractiilor: "Insula Distractiilor",
   wuling: "Wuling",
   instruireautoplatform: "Instruire Auto Platforma",
   instruireautoschool: "Instruire Auto",
};

const serviceSeo = {
   automation: {
      title: "Automatizari AI si workflow-uri pentru business | Zotaris",
      description:
         "Automatizam procese repetitive, conectam CRM-uri, formulare, email, notificari, rapoarte si tool-uri interne in fluxuri digitale clare.",
   },
   platforms: {
      title: "Platforme web custom si dashboard-uri | Zotaris",
      description:
         "Construim platforme web custom, conturi de utilizator, dashboard-uri, panouri interne si sisteme digitale scalabile pentru echipe.",
   },
   websites: {
      title: "Site-uri moderne, rapide si optimizate SEO | Zotaris",
      description:
         "Cream site-uri corporate, landing pages, marketplace-uri si pagini de produs cu design premium, performanta buna si structurare SEO.",
   },
   design: {
      title: "UI/UX design si product design | Zotaris",
      description:
         "Proiectam interfete, fluxuri, prototipuri si design systems care fac produsele digitale mai clare, coerente si usor de folosit.",
   },
   courses: {
      title: "Cursuri practice de AI si automatizare | Zotaris",
      description:
         "Training practic pentru persoane, echipe si companii care vor sa aplice AI, automatizari, prompturi si tool-uri in munca reala.",
   },
   consulting: {
      title: "Consultanta digitala si strategie pentru business | Zotaris",
      description:
         "Analizam procese, tool-uri, date si obiective pentru a construi roadmap-uri digitale clare si decizii mai bune.",
   },
};

function setMeta(selector, attributes) {
   let node = document.head.querySelector(selector);
   if (!node) {
      node = document.createElement("meta");
      document.head.appendChild(node);
   }

   Object.entries(attributes).forEach(([key, value]) => {
      node.setAttribute(key, value);
   });
}

function setLink(selector, attributes) {
   let node = document.head.querySelector(selector);
   if (!node) {
      node = document.createElement("link");
      document.head.appendChild(node);
   }

   Object.entries(attributes).forEach(([key, value]) => {
      node.setAttribute(key, value);
   });
}

function getSeoForPath(pathname) {
   if (pathname === "/projects") {
      return {
         title: "Proiecte digitale, site-uri si platforme realizate | Zotaris",
         description:
            "Vezi proiecte Zotaris: site-uri, platforme digitale, prezentari web, design, frontend si experiente construite pentru claritate si conversie.",
         type: "CollectionPage",
      };
   }

   if (pathname.startsWith("/projects/")) {
      const slug = pathname.replace("/projects/", "");
      const project = getProjectBySlug(slug);
      const title = projectTitles[project?.id] || "Proiect digital";
      return {
         title: `${title} | Proiect Zotaris`,
         description: `${title} este un proiect digital Zotaris construit pentru claritate, incredere, experienta buna si conversie.`,
         type: "CreativeWork",
      };
   }

   if (pathname.startsWith("/services/")) {
      const slug = pathname.replace("/services/", "");
      const service = getServiceBySlug(slug);
      if (serviceSeo[service?.id]) {
         return { ...serviceSeo[service.id], type: "Service" };
      }
   }

   return {
      title:
         "Zotaris | Automatizari AI, site-uri, platforme custom si infrastructura digitala",
      description: DEFAULT_DESCRIPTION,
      type: "WebSite",
   };
}

function makeJsonLd({ pathname, canonical, title, description, type }) {
   const baseOrganization = {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
      logo: OG_IMAGE,
      email: "contact@zotaris.com",
      sameAs: [
         "https://www.instagram.com/zotaris.com",
         "https://www.linkedin.com/company/zotaris",
         "https://www.facebook.com/zotaris",
      ],
      knowsAbout: [
         "AI automation",
         "web development",
         "custom web platforms",
         "UI/UX design",
         "digital consulting",
         "CRM integrations",
         "business workflows",
      ],
   };

   const webPage = {
      "@context": "https://schema.org",
      "@type": type,
      name: title,
      description,
      url: canonical,
      isPartOf: {
         "@type": "WebSite",
         name: SITE_NAME,
         url: SITE_URL,
      },
   };

   if (pathname.startsWith("/services/")) {
      webPage.provider = {
         "@type": "Organization",
         name: SITE_NAME,
         url: SITE_URL,
      };
      webPage.areaServed = ["Moldova", "Romania", "Europe"];
      webPage.serviceType = title.replace(" | Zotaris", "");
   }

   return [baseOrganization, webPage];
}

export default function SEO() {
   const location = useLocation();
   const pathname = location.pathname || "/";
   const seo = useMemo(() => getSeoForPath(pathname), [pathname]);
   const canonical = `${SITE_URL}${pathname === "/" ? "/" : pathname}`;

   useEffect(() => {
      document.documentElement.lang = "ro";
      document.title = seo.title;

      setMeta('meta[name="description"]', {
         name: "description",
         content: seo.description,
      });
      setMeta('meta[name="keywords"]', {
         name: "keywords",
         content: DEFAULT_KEYWORDS,
      });
      setMeta('meta[name="robots"]', {
         name: "robots",
         content: "index, follow, max-image-preview:large",
      });
      setLink('link[rel="canonical"]', {
         rel: "canonical",
         href: canonical,
      });

      setMeta('meta[property="og:title"]', {
         property: "og:title",
         content: seo.title,
      });
      setMeta('meta[property="og:description"]', {
         property: "og:description",
         content: seo.description,
      });
      setMeta('meta[property="og:url"]', {
         property: "og:url",
         content: canonical,
      });
      setMeta('meta[property="og:type"]', {
         property: "og:type",
         content: seo.type === "WebSite" ? "website" : "article",
      });
      setMeta('meta[property="og:site_name"]', {
         property: "og:site_name",
         content: SITE_NAME,
      });
      setMeta('meta[property="og:image"]', {
         property: "og:image",
         content: OG_IMAGE,
      });
      setMeta('meta[property="og:locale"]', {
         property: "og:locale",
         content: "ro_MD",
      });

      setMeta('meta[name="twitter:card"]', {
         name: "twitter:card",
         content: "summary_large_image",
      });
      setMeta('meta[name="twitter:title"]', {
         name: "twitter:title",
         content: seo.title,
      });
      setMeta('meta[name="twitter:description"]', {
         name: "twitter:description",
         content: seo.description,
      });
      setMeta('meta[name="twitter:image"]', {
         name: "twitter:image",
         content: OG_IMAGE,
      });

      const scriptId = "zotaris-json-ld";
      let script = document.getElementById(scriptId);
      if (!script) {
         script = document.createElement("script");
         script.id = scriptId;
         script.type = "application/ld+json";
         document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(
         makeJsonLd({ pathname, canonical, ...seo }),
      );
   }, [canonical, pathname, seo]);

   return null;
}
